/** Only pagination may reuse a visible result. Prices never cross an auth/filter scope. */
export type CategoryPlaceholderScope = {
  initialized: boolean;
  categoryId: number;
  locale: string;
  customerId: number | null;
  authToken: string | null;
  queryIdentity: string;
  attributesIdentity: string;
  urlFilterIdentity: string;
};

export function canKeepCategoryPlaceholder(
  previous: unknown,
  current: CategoryPlaceholderScope,
): previous is CategoryPlaceholderScope {
  if (!previous || typeof previous !== 'object' || !current.initialized) return false;
  const scope = previous as Partial<CategoryPlaceholderScope>;
  return (
    scope.initialized === true &&
    scope.categoryId === current.categoryId &&
    scope.locale === current.locale &&
    scope.customerId === current.customerId &&
    scope.authToken === current.authToken &&
    scope.queryIdentity === current.queryIdentity &&
    scope.attributesIdentity === current.attributesIdentity &&
    scope.urlFilterIdentity === current.urlFilterIdentity
  );
}

export function getCategoryPlaceholder<T>(
  previousData: T | undefined,
  previousScope: unknown,
  currentScope: CategoryPlaceholderScope,
): T | undefined {
  return canKeepCategoryPlaceholder(previousScope, currentScope) ? previousData : undefined;
}

export function canLoadMoreCategoryProducts(state: {
  ready: boolean;
  pending: boolean;
  placeholder: boolean;
  error: boolean;
  fetching: boolean;
}) {
  return state.ready && !state.pending && !state.placeholder && !state.error && !state.fetching;
}

/** A disabled query can still hold private cached data; enabled/inert are not privacy guards. */
export function getVisibleCategoryData<T>(state: {
  ready: boolean;
  queryData?: T;
  errorPlaceholder?: T;
  publicSeed?: T;
}): T | undefined {
  return state.ready ? (state.queryData ?? state.errorPlaceholder) : state.publicSeed;
}
